// ReviewComponent.jsx
import React, { useState, useEffect } from 'react';
import axios from 'axios';

import './style/Review.css';
const ReviewComponent = ({ productId }) => {
    const [reviews, setReviews] = useState([]);
    const [averageRating, setAverageRating] = useState(0);
    const [totalReviews, setTotalReviews] = useState(0);
    const [userReview, setUserReview] = useState(null);
    const [rating, setRating] = useState(5);
    const [comment, setComment] = useState('');
    const [replyText, setReplyText] = useState('');
    const [editingReviewId, setEditingReviewId] = useState(null);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        fetchReviews();
        checkUserReview();
        const token = localStorage.getItem('token');
        setIsAuthenticated(!!token);
    }, [productId]);
     const api = "https://mkbackend-production.up.railway.app"
       

    const fetchReviews = async () => {
        try {
            const response = await axios.get(`${api}/products/${productId}/reviews`);
            if (response.data.success) {
                setReviews(response.data.reviews);
                setAverageRating(response.data.averageRating || 0);
                setTotalReviews(response.data.totalReviews || 0);
            }
        } catch (error) {
            console.error('Error fetching reviews:', error);
        }
    };

    const checkUserReview = async () => {
        try {
            const token = localStorage.getItem('token');
            if (!token) return;

            const response = await axios.get(
                `${api}/products/${productId}/user-review`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            
            if (response.data.success && response.data.hasReviewed) {
                setUserReview(response.data.review);
                setRating(response.data.review.rating);
                setComment(response.data.review.comment);
            }
        } catch (error) {
            console.error('Error checking user review:', error);
        }
    };

    const handleSubmitReview = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            const token = localStorage.getItem('token');
            if (!token) {
                alert('Please login to submit a review');
                setLoading(false);
                return;
            }

            const url = userReview 
                ? `${api}/reviews/${userReview._id}`
                : `${api}/products/${productId}/reviews`;
            
            const method = userReview ? 'put' : 'post';

            const response = await axios({
                method: method,
                url: url,
                data: { rating, comment },
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data.success) {
                alert(userReview ? 'Review updated successfully!' : 'Review submitted successfully!');
                fetchReviews();
                checkUserReview();
                if (!userReview) {
                    setComment('');
                    setRating(5);
                }
            }
        } catch (error) {
            console.error('Error submitting review:', error);
            alert(error.response?.data?.message || 'Error submitting review');
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteReview = async (reviewId) => {
        if (!window.confirm('Are you sure you want to delete this review?')) return;

        try {
            const token = localStorage.getItem('token');
            const response = await axios.delete(
                `${api}/reviews/${reviewId}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (response.data.success) {
                alert('Review deleted successfully');
                fetchReviews();
                setUserReview(null);
                setComment('');
                setRating(5);
            }
        } catch (error) {
            console.error('Error deleting review:', error);
            alert(error.response?.data?.message || 'Error deleting review');
        }
    };

    const handleAddReply = async (reviewId) => {
        if (!replyText.trim()) return;

        try {
            const token = localStorage.getItem('token');
            const response = await axios.post(
                `${api}/reviews/${reviewId}/replies`,
                { text: replyText.trim() },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (response.data.success) {
                setReplyText('');
                fetchReviews();
            }
        } catch (error) {
            console.error('Error adding reply:', error);
            alert(error.response?.data?.message || 'Error adding reply');
        }
    };

    const renderStars = (rating, interactive = false) => {
        return [1, 2, 3, 4, 5].map((star) => (
            <span
                key={star}
                onClick={interactive ? () => setRating(star) : undefined}
                style={{
                    cursor: interactive ? 'pointer' : 'default',
                    color: star <= rating ? '#ffc107' : '#e4e5e9',
                    fontSize: '24px'
                }}
            >
                ★
            </span>
        ));
    };

    return (
        <div className="reviews-container">
            <h3>Reviews & Ratings</h3>
            
            {/* Rating Summary */}
            <div className="rating-summary">
                <div className="average-rating">
                    <span style={{ fontSize: '32px', fontWeight: 'bold' }}>
                        {averageRating.toFixed(1)}
                    </span>
                    <div>{renderStars(Math.round(averageRating))}</div>
                    <span>{totalReviews} reviews</span>
                </div>
            </div>

            {/* Review Form */}
            {isAuthenticated ? (
                <div className="review-form">
                    <h4>{userReview ? 'Edit Your Review' : 'Write a Review'}</h4>
                    <form onSubmit={handleSubmitReview}>
                        <div className="rating-input">
                            <label>Your Rating:</label>
                            <div>{renderStars(rating, true)}</div>
                        </div>
                        <div className="comment-input">
                            <textarea
                                value={comment}
                                onChange={(e) => setComment(e.target.value)}
                                placeholder="Share your experience with this product..."
                                required
                                rows="4"
                                maxLength="500"
                            />
                            <small>{comment.length}/500</small>
                        </div>
                        <button type="submit" disabled={loading}>
                            {loading ? 'Submitting...' : userReview ? 'Update Review' : 'Submit Review'}
                        </button>
                        {userReview && (
                            <button
                                type="button"
                                onClick={() => handleDeleteReview(userReview._id)}
                                style={{ marginLeft: '10px', backgroundColor: '#dc3545' }}
                            >
                                Delete Review
                            </button>
                        )}
                    </form>
                </div>
            ) : (
                <p>Please login to write a review</p>
            )}

            {/* Reviews List */}
            <div className="reviews-list">
                {reviews.length === 0 ? (
                    <p>No reviews yet. Be the first to review!</p>
                ) : (
                    reviews.map((review) => (
                        <div key={review._id} className="review-item">
                            <div className="review-header">
                                <strong>{review.userName}</strong>
                                <span>{renderStars(review.rating)}</span>
                                <small>{new Date(review.createdAt).toLocaleDateString()}</small>
                            </div>
                            <p className="review-comment">{review.comment}</p>
                            
                            {/* Replies */}
                            {review.replies.length > 0 && (
                                <div className="replies">
                                    {review.replies.map((reply, index) => (
                                        <div key={index} className="reply-item">
                                            <strong>{reply.userName}</strong>
                                            <p>{reply.text}</p>
                                            <small>{new Date(reply.createdAt).toLocaleDateString()}</small>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {/* Reply Form */}
                            {isAuthenticated && (
                                <div className="reply-form">
                                    <input
                                        type="text"
                                        value={replyText}
                                        onChange={(e) => setReplyText(e.target.value)}
                                        placeholder="Write a reply..."
                                        onKeyPress={(e) => {
                                            if (e.key === 'Enter') {
                                                handleAddReply(review._id);
                                            }
                                        }}
                                    />
                                    <button onClick={() => handleAddReply(review._id)}>Reply</button>
                                </div>
                            )}
                        </div>
                    ))
                )}
            </div>

            <style jsx>{`
                .reviews-container {
                    max-width: 800px;
                    margin: 20px auto;
                    padding: 20px;
                    font-family: Arial, sans-serif;
                }
                
                .rating-summary {
                    background: #f8f9fa;
                    padding: 20px;
                    border-radius: 8px;
                    margin-bottom: 30px;
                }
                
                .average-rating {
                    text-align: center;
                }
                
                .review-form {
                    background: white;
                    padding: 20px;
                    border: 1px solid #ddd;
                    border-radius: 8px;
                    margin-bottom: 30px;
                }
                
                .rating-input {
                    margin-bottom: 15px;
                }
                
                .comment-input textarea {
                    width: 100%;
                    padding: 10px;
                    border: 1px solid #ddd;
                    border-radius: 4px;
                    resize: vertical;
                }
                
                .review-item {
                    background: white;
                    padding: 15px;
                    border: 1px solid #eee;
                    border-radius: 8px;
                    margin-bottom: 15px;
                }
                
                .review-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 10px;
                }
                
                .replies {
                    margin: 10px 0 10px 20px;
                    padding-left: 15px;
                    border-left: 3px solid #007bff;
                }
                
                .reply-item {
                    background: #f8f9fa;
                    padding: 10px;
                    border-radius: 4px;
                    margin-bottom: 10px;
                }
                
                .reply-form {
                    display: flex;
                    gap: 10px;
                    margin-top: 10px;
                }
                
                .reply-form input {
                    flex: 1;
                    padding: 8px;
                    border: 1px solid #ddd;
                    border-radius: 4px;
                }
                
                button {
                    padding: 10px 20px;
                    background: #007bff;
                    color: white;
                    border: none;
                    border-radius: 4px;
                    cursor: pointer;
                }
                
                button:hover {
                    background: #0056b3;
                }
                
                button:disabled {
                    opacity: 0.6;
                    cursor: not-allowed;
                }
            `}</style>
        </div>
    );
};

export default ReviewComponent;